import { useCallback, useMemo, useRef } from 'react';

import { fromPromise } from 'xstate5';

import { type DeliveryOrderDetailInput } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  reportOutOfStockIngredients,
  useCustomizationMachineLogic,
} from '@sg/shared-fe-features-customization';

import { useLastInteractedStore } from '../../../../context';
import { addCartLineItemsToBundleAttribution } from '../../../../helpers/bundle-attribution';
import { useCart } from '../../../../hooks';
import { useAddBundleProductToBag } from '../useAddBundleProductToBag';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides the bundle machine with the actor that adds its products to the bag,
 * and marks what it added once the run ends.
 */
export const useBundleAddToBagActors = () => {
  const lastInteractedStore = useLastInteractedStore();
  const addBundleProductToBag = useAddBundleProductToBag();
  const { cart } = useCart();

  // ─── Refs ────────────────────────────────────────────────────────────

  const deliveryDetailsRef = useRef<DeliveryOrderDetailInput | undefined>(undefined);

  // NOTE: Spawned customization machines keep the actors they started with, so
  //       their add reads the latest one through this ref.
  const addBundleProductToBagRef = useRef(addBundleProductToBag);

  addBundleProductToBagRef.current = addBundleProductToBag;

  // NOTE: The bundle machine keeps the actions it started with, so the attribution
  //       below reads the latest bag through this ref.
  const cartRef = useRef(cart);

  cartRef.current = cart;

  // ─── Derived ─────────────────────────────────────────────────────────

  const customizationImplementations = useMemo(() => {
    return {
      actors: {
        addToBag: fromPromise<void, AddToBagInput>(async (args) => {
          await addBundleProductToBagRef.current({
            ...args.input,
            deliveryDetails: deliveryDetailsRef.current,
          });
        }),
      },

      // NOTE: Out-of-stock default ingredients are left off, and the bundle shows them on the
      //       selected product rather than asking first.
      actions: { onOutOfStockIngredientsFound: reportOutOfStockIngredients },
    };
  }, []);

  const customization = useCustomizationMachineLogic(customizationImplementations);

  // ─── Handlers ────────────────────────────────────────────────────────

  /**
   * Freezes where the products go for the rest of the add, so a store change
   * midway can't split the bundle across orders.
   */
  const captureDeliveryDetails = useCallback(() => {
    deliveryDetailsRef.current = getDeliveryDetails(lastInteractedStore);
  }, [lastInteractedStore]);

  const attributeAddedLineItems = useCallback((addedProductIds: string[]) => {
    addCartLineItemsToBundleAttribution({
      orderId: cartRef.current?.id,
      cartLineItemIds: getAddedCartLineItemIds({
        lineItems: cartRef.current?.lineItems,
        addedProductIds,
      }),
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  return { customization, captureDeliveryDetails, attributeAddedLineItems };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAddedCartLineItemIds(params: GetAddedCartLineItemIdsParams) {
  const { lineItems = [], addedProductIds } = params;

  return Array.from(countProductIds(addedProductIds)).flatMap(([productId, addedCount]) =>
    lineItems
      .filter((lineItem) => lineItem.product?.id === productId)
      .slice(-addedCount)
      .map(getLineItemId),
  );
}

function countProductIds(productIds: string[]) {
  return new Map(
    productIds.map((productId) => [
      productId,
      productIds.filter((otherProductId) => otherProductId === productId).length,
    ]),
  );
}

function getLineItemId(lineItem: CartLineItem) {
  return lineItem.id;
}

function getDeliveryDetails(
  lastInteractedStore: LastInteractedStore,
): DeliveryOrderDetailInput | undefined {
  const { addressId, deliveryFee, vendor, vendorRestaurantId } = lastInteractedStore ?? {};

  if (!addressId) return;

  return {
    tip: 0,
    addressId,
    vendor: vendor ?? '',
    vendorRestaurantId: vendorRestaurantId ?? '',
    deliveryFee: deliveryFee ?? 0,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type AddToBagInput = CustomizationMachineTypes.AddToBagInput;

type GetAddedCartLineItemIdsParams = {
  lineItems: readonly CartLineItem[] | undefined;
  addedProductIds: string[];
};

type CartLineItem = NonNullable<ReturnType<typeof useCart>['cart']>['lineItems'][number];

type LastInteractedStore = ReturnType<typeof useLastInteractedStore>;
